import { useSyncExternalStore, type ReactNode } from 'react'
import type { Platform } from '../core/bindings.ts'
import { displayChord } from '../core/keymap.ts'
import { hintsFor, shortcutMap, type Hint } from '../core/hints.ts'
import { useChrome, useChromeState, useShell, useShellState } from './hooks.ts'

/// The hints for what the translator is doing now, at most four, and the labelled entry to the
/// full map (docs/design/ux-review.md, unit 6).
export function Hints() {
  const shell = useShell()
  const state = useShellState()
  const report = useSyncExternalStore(shell.editorStore.subscribe, shell.editorStore.get, shell.editorStore.get)
  const row = shell.activeRow
  const hints = hintsFor({
    target: shell.targetName,
    completion: report.completion !== null,
    entry: state.entry !== null,
    splitting: state.splitting !== null,
    splitKeeps: shell.hasTarget,
    finding: state.focus === 'find',
    review: row?.review ?? null,
    editable: shell.editable,
    missing: report.missing.length > 0 && !report.empty,
    unlock: shell.registry.get('segment.unlock')?.when?.(shell) ?? false,
  })
  return (
    <span className="hints">
      {hints.map(h => <span key={h.label} className="hint">{keysOf(shell, h).map(k => <kbd key={k}>{k}</kbd>)} {h.label}</span>)}
      <button className="sbtn shortcuts" data-command="shortcuts.open" onClick={() => shell.command('shortcuts.open')}>Shortcuts</button>
    </span>
  )
}

function keysOf(shell: ReturnType<typeof useShell>, hint: Hint): string[] {
  if (hint.keys) return hint.keys
  return hint.commands.flatMap(id => {
    const chord = shell.shownChord(id)
    return chord ? [chord] : []
  })
}

/// The sheet's line on the other platform's modifier.
const otherPlatform = (platform: Platform) => (platform === 'mac' ? 'Cmd is Ctrl on Windows.' : 'Ctrl is Cmd on a Mac.')

/// The keys the suggestions list takes while open, the shell's last section.
const SUGGESTIONS = (
  <section className="task">
    <h3>In the suggestions list</h3>
    <table><tbody>
      <tr><td>Choose a suggestion</td><td><kbd>Up</kbd> <kbd>Down</kbd></td></tr>
      <tr><td>Insert it</td><td><kbd>Enter</kbd> <kbd>Tab</kbd></td></tr>
      <tr><td>Close the list</td><td><kbd>Esc</kbd></td></tr>
    </tbody></table>
  </section>
)

interface SheetProps {
  /// The line on the other platform's modifiers.
  platformNote?: (platform: Platform) => string
  /// The sections after the menus': keys no command holds.
  sections?: ReactNode
}

/// Every chord grouped by menu, with the keys the suggestions popup and the split picker take
/// while open. The aligner's page passes its own line and its grid's and edit box's keys.
export function ShortcutsSheet({ platformNote = otherPlatform, sections = SUGGESTIONS }: SheetProps) {
  const shell = useChrome()
  const state = useChromeState()
  if (!state.shortcuts) return null
  const close = () => shell.set({ shortcuts: false })
  const chord = (id: string) => {
    const c = shell.registry.chord(id, shell.platform)
    return c ? displayChord(c, shell.platform) : null
  }
  // A command bound to a place the screen does not have, the menu bar's chord on the Home, is left
  // out of the help there. Its `when` takes the shell's own Shell, which this is on that screen.
  const tasks = shortcutMap(shell.registry.all().filter(c => c.id !== 'menubar.focus' || (c.when?.(shell as never) ?? true)), chord, shell.menuHost)
  return (
    <div className="dialog" onMouseDown={e => { if (e.target === e.currentTarget) close() }}>
      <div className="dbox shortcuts" role="dialog" aria-labelledby="shortcuts-title" onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close() } }}>
        <h2 id="shortcuts-title">Keyboard shortcuts</h2>
        <p className="muted">{platformNote(shell.platform)} Every command is also in the palette, on {chord('palette.open')}.</p>
        <div className="tasks">
          {tasks.map(task => (
            <section key={task.title} className="task">
              <h3>{task.title}</h3>
              <table><tbody>{task.rows.map(([title, keys, help]) => <tr key={title}><td>{title}{help ? <div className="help">{help}</div> : null}</td><td><kbd>{keys}</kbd></td></tr>)}</tbody></table>
            </section>
          ))}
          {sections}
        </div>
        <div className="dactions"><button autoFocus onClick={close}>Close</button></div>
      </div>
    </div>
  )
}
